import {
  Badge,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useCreateExclusiveDealApi } from 'apis/exclusive-deals';
import type { ExclusiveDealSummary, QuotationSupplierProduct } from 'apis/restaurant-inquiries';
import { quotationSuppliersEndpoint } from 'apis/restaurant-inquiries';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

interface Props {
  product: QuotationSupplierProduct;
  countryCode: string; // required by POST /cms/exclusive-deals — callers hide this component without it
  defaultDealPrice?: string;
  onCreated: (deal: ExclusiveDealSummary) => void;
}

// Inline deal creation for a deal-less product, right in the PriceLadder's
// Deal slot: one required price, one activation toggle, no navigation to the
// Exclusive Deals tab. Cashback isn't collected here — it's a separate,
// optional decision the CMS still makes in the Exclusive Deals tab. The full
// deal form (expiry, near-expiry, distributors) stays over there too — this
// covers the workbench case: "we negotiated a price, lock it in as a deal
// while quoting" — active to publish it to restaurants now, or inactive to
// just record the negotiated price for later.
export const InlineAddDealPopover = ({ product, countryCode, defaultDealPrice, onCreated }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();
  const { mutate: createDeal, isLoading } = useCreateExclusiveDealApi();
  const [dealPrice, setDealPrice] = useState('');
  const [isActive, setIsActive] = useState(true);

  const openWithDefaults = () => {
    setDealPrice(defaultDealPrice || '');
    setIsActive(true);
    onOpen();
  };

  const canSubmit = Number(dealPrice) > 0;

  const submit = () => {
    const price = Number(dealPrice);
    if (!canSubmit) return; // the disabled button already communicates it
    createDeal(
      { body: { productId: product.id, countryCode, dealPrice: price, isActive } },
      {
        onSuccess: (res: any) => {
          const dto = res?.data ?? res ?? {};
          // The quotation catalog caches refetch lazily; the caller patches the
          // in-flight selection so the ladder flips to DEAL immediately.
          queryClient.invalidateQueries([quotationSuppliersEndpoint]);
          onCreated({
            id: Number(dto.id) || 0,
            dealPrice: dto.dealPrice ?? price,
            originalPrice: dto.originalPrice ?? null,
            cashbackPercent: dto.cashbackPercent ?? null,
            productExpiryDate: dto.productExpiryDate ?? null,
            lastModifiedAt: dto.lastModifiedAt ?? new Date().toISOString(),
            isActive: dto.isActive ?? isActive,
          });
          onClose();
        },
      }
    );
  };

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement='bottom-start' isLazy>
      <PopoverTrigger>
        <Button size='xs' variant='link' colorScheme='green' onClick={openWithDefaults}>
          + Add deal
        </Button>
      </PopoverTrigger>
      <PopoverContent w='260px'>
        <PopoverArrow />
        <PopoverBody p='3'>
          <Text fontSize='sm' fontWeight='semibold' mb='1'>
            New exclusive deal
          </Text>
          <Text fontSize='xs' color='gray.500' noOfLines={2} mb='2'>
            {product.nameEn || `Product #${product.id}`} · {countryCode}
          </Text>
          <FormControl isRequired mb='3'>
            <FormLabel fontSize='xs' mb='1'>
              Deal price (AED)
            </FormLabel>
            <Input
              size='sm'
              value={dealPrice}
              onChange={(e) => setDealPrice(numOnly(e.target.value))}
              placeholder='e.g. 9.50'
              autoFocus
            />
          </FormControl>
          <FormControl display='flex' alignItems='center' mb='1'>
            <Switch
              id='deal-active-toggle'
              size='sm'
              colorScheme='green'
              isChecked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              me='2'
            />
            <FormLabel htmlFor='deal-active-toggle' mb='0' fontSize='sm' cursor='pointer'>
              Deal active
            </FormLabel>
            <Badge ml='auto' colorScheme={isActive ? 'green' : 'gray'} variant='subtle' fontSize='2xs'>
              {isActive ? 'Visible to restaurants' : 'Saved, not published'}
            </Badge>
          </FormControl>
          <Text fontSize='xs' color='gray.500' mb='3'>
            {isActive
              ? 'Restaurants will see this price right away.'
              : 'Turn this off to record the negotiated price without publishing it yet — flip it on later from Exclusive Deals.'}
          </Text>
          <Flex gap='2' justify='flex-end'>
            <Button size='xs' variant='outline' onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button size='xs' colorScheme='green' onClick={submit} isLoading={isLoading} isDisabled={!canSubmit}>
              Create deal
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
